import React from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Box,
} from "@mui/material";
import { useAnalytics } from "use-analytics";
import {
  useCompleteInteractionFlagMutation,
  useInteractionFlag,
} from "../../crud/interactions/hooks";
import { InteractionFlagNames } from "../../crud/interactions/types";
import { HookSelect } from "../HookSelect";
import { parseError } from "../../apiUtils";

interface UserSourceFormInput {
  source: string;
  otherSource?: string;
  moreInformation?: string;
}

const SOURCES = [
  "Search Engine",
  "Friend or Colleague",
  "Social Media",
  "Google Ad",
  "Dask.org",
  "Event",
  "YouTube",
  "Podcast",
  "Other",
];

export const UserSourceDialog = (): React.ReactElement | null => {
  const analytics = useAnalytics();
  const hasSeenDialog = useInteractionFlag(
    InteractionFlagNames.HasSeenUserSourceDialog,
  );
  const completeHasSeenDialogMutation = useCompleteInteractionFlagMutation(
    InteractionFlagNames.HasSeenUserSourceDialog,
  );

  const { register, handleSubmit, watch, control } =
    useForm<UserSourceFormInput>({
      mode: "all",
      defaultValues: {
        source: "",
      },
    });

  const source = watch("source");

  const onSubmit = async (data: UserSourceFormInput) => {
    analytics.track("user-source", {
      source: data.source,
      otherSource: data.otherSource,
      moreInformation: data.moreInformation,
    });

    handleClose();
  };

  const handleClose = async () => {
    // Mark INTERACTION_FLAG as completed will close
    await completeHasSeenDialogMutation.mutate({});
  };

  if (hasSeenDialog.isLoading || hasSeenDialog.data?.completed) {
    return null;
  }

  const getAdditionalField = () => {
    switch (source) {
      case "":
        return null;
      case "Other":
        return (
          <TextField
            label="Please specify"
            variant="outlined"
            {...register("otherSource")}
          />
        );
      default:
        return (
          <TextField
            label="More information"
            variant="outlined"
            {...register("moreInformation")}
          />
        );
    }
  };

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle sx={{ p: 3 }}>Welcome to Coiled!</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box sx={{ width: 400 }}>
            <Stack spacing={2}>
              <Typography>How did you learn about us?</Typography>
              <HookSelect control={control} name="source">
                {SOURCES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </HookSelect>
              {getAdditionalField()}
            </Stack>
          </Box>

          {completeHasSeenDialogMutation.isError && (
            <Typography color="error">
              {parseError(completeHasSeenDialogMutation.error)}
            </Typography>
          )}
          <DialogActions sx={{ mt: 3, p: 0 }}>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Skip
            </Button>
            <Button type="submit">Submit</Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
